import { memo, useMemo } from 'react'

import { DirectionsRun } from '@mui/icons-material'
import { Box, LinearProgress, Paper, Typography } from '@mui/material'
import { MediaEventInfo } from '@nativewaves/nw-cloud-sdk/showcase'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { DATE_TIME_FORMAT } from '@shared/utils/support'

type EventProgressProps = {
  event?: MediaEventInfo
}

const EventProgressRaw = ({ event }: EventProgressProps) => {
  const { t } = useTranslation(['domain'])

  const hasStarted = event?.startTime && Date.now() - Number(event?.startTime) * 1000 > 0
  const openEnd = !event?.endTime
  const hasEnded = event?.ended
  const completeDuration =
    !!event?.endTime && !!event?.startTime && new Date(event?.endTime).valueOf() - new Date(event?.startTime).valueOf()

  const startTimeFormatted = useMemo(
    () => (event?.startTime ? format(new Date(event.startTime), DATE_TIME_FORMAT) : undefined),
    [event?.startTime],
  )

  const endTimeFormatted = useMemo(
    () => (event?.endTime ? format(new Date(event.endTime), DATE_TIME_FORMAT) : undefined),
    [event?.endTime],
  )

  return (
    <Paper sx={{ width: '100%', borderRadius: 1, py: 1, px: 1 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="heading-md">{t('domain:Showcase.Collection.progress')}</Typography>
        <DirectionsRun />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ display: 'block' }} variant="caption">
            {t('domain:Showcase.Collection.start')}
          </Typography>
          {!startTimeFormatted ? (
            <Typography>{t('domain:Showcase.Collection.noStartTime')}</Typography>
          ) : (
            <Typography variant="heading-sm">{startTimeFormatted}</Typography>
          )}
        </Box>

        <Box sx={{ mb: 1, textAlign: 'end' }}>
          <Typography sx={{ display: 'block' }} variant="caption">
            {t('domain:Showcase.Collection.end')}
          </Typography>
          <Typography variant="heading-sm">{endTimeFormatted ?? t('domain:Showcase.Collection.openEnd')}</Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="buffer"
        value={
          hasEnded
            ? 100
            : hasStarted
              ? openEnd
                ? 50
                : completeDuration && event?.duration
                  ? Math.floor(((event?.duration * 1000) / completeDuration) * 100)
                  : 0
              : 0
        }
        valueBuffer={
          hasStarted && completeDuration && event?.duration
            ? Math.floor(((event?.duration * 1000) / completeDuration) * 100)
            : openEnd
              ? 60
              : 100
        }
        color={hasEnded ? 'success' : 'info'}
      />
    </Paper>
  )
}

export const EventProgress = memo(EventProgressRaw)
